"use client";
/**
 * MemoryMenu — control for the AI's cross-session chat memory.
 *
 * Lets the user:
 *  - toggle "remember past conversations" on/off
 *  - clear the memory of everything before now ("forget" action)
 *
 * Reads/writes the backend's /memory endpoint (same ASK endpoint used for
 * chat). Memory itself is assembled server-side and injected into the LLM
 * prompt; this UI only manages the user's preference + forget anchor.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { BrainCircuit } from "lucide-react";
import { getMemoryPrefs, updateMemoryPrefs, type MemoryPrefs } from "@/modes/ask/api";

export function MemoryMenu() {
  const rootRef   = useRef<HTMLDivElement>(null);
  const [open,    setOpen]    = useState(false);
  const [prefs,   setPrefs]   = useState<MemoryPrefs | null>(null);
  const [busy,    setBusy]    = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  // Load preferences whenever the popover opens again (async only — React
  // lint rule forbids synchronous setState inside the effect body).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const p = await getMemoryPrefs();
        if (!cancelled) setPrefs(p);
      } catch {
        if (!cancelled) setPrefs((prev) => prev ?? { enabled: true });
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setConfirmClear(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setConfirmClear(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggleEnabled = useCallback(async (enabled: boolean) => {
    setBusy(true);
    try {
      const next = await updateMemoryPrefs(enabled ? "enable" : "disable");
      setPrefs(next);
    } catch {
      // keep previous state on failure
    } finally {
      setBusy(false);
    }
  }, []);

  const clearMemory = useCallback(async () => {
    setBusy(true);
    try {
      const next = await updateMemoryPrefs("clear");
      setPrefs(next);
      setConfirmClear(false);
    } catch {
      // keep previous state on failure
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <div ref={rootRef} className="absolute top-3 right-16 z-40">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat memory settings"
        title="Chat memory — does the AI remember your past conversations?"
        className="h-9 w-9 rounded-xl border border-border bg-card text-muted-foreground shadow-sm flex items-center justify-center transition-colors hover:bg-accent hover:text-foreground"
      >
        <BrainCircuit className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-72 rounded-xl border border-border bg-card shadow-lg p-3 space-y-3 z-50">
          <div className="text-sm font-medium">Chat memory</div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            When enabled, the AI remembers your previous conversations so answers can build on what you asked before.
          </p>

          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span className="text-sm">Remember past conversations</span>
            <button
              type="button"
              role="switch"
              aria-checked={prefs?.enabled ?? true}
              disabled={busy || prefs === null}
              onClick={() => toggleEnabled(prefs ? !prefs.enabled : true)}
              className={`relative h-5 w-9 rounded-full transition-colors disabled:opacity-40 ${
                prefs?.enabled ?? true ? "bg-indigo-500" : "bg-muted"
              }`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  prefs?.enabled ?? true ? "translate-x-[18px]" : "translate-x-0.5"
                }`}
              />
            </button>
          </label>

          <div className="border-t border-border/60 pt-2 space-y-1.5">
            {!confirmClear ? (
              <button
                type="button"
                onClick={() => setConfirmClear(true)}
                disabled={busy}
                className="w-full text-left text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/5 rounded-lg px-2 py-1.5 disabled:opacity-40 transition-colors"
              >
                Clear memory
              </button>
            ) : (
              <div className="flex items-center justify-between gap-2 px-2">
                <span className="text-xs text-muted-foreground">Forget everything from before now?</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => setConfirmClear(false)}
                    disabled={busy}
                    className="text-xs text-muted-foreground hover:text-foreground px-1.5 py-1 disabled:opacity-40"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={clearMemory}
                    disabled={busy}
                    className="text-xs font-medium text-red-400 hover:text-red-300 px-1.5 py-1 disabled:opacity-40"
                  >
                    Yes, clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {prefs === null && <div className="text-[11px] text-muted-foreground animate-pulse">Loading…</div>}
        </div>
      )}
    </div>
  );
}